import type { ClinicalDraftFields } from "./api";

export interface LocalClinicalDraft {
  organizationId: string;
  petId: string;
  draftId: string | null;
  revision: number | null;
  fields: ClinicalDraftFields;
  savedAt: string;
}

interface StoredVault {
  version: 1;
  iv: string;
  ciphertext: string;
}

const VAULT_TTL_MS = 12 * 60 * 60 * 1000;

function storageKey(scope: string): string {
  return `tappet-crm-clinical-draft:${scope}`;
}

function keyStorageKey(scope: string): string {
  return `tappet-crm-clinical-draft-key:${scope}`;
}

function encode(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function decode(value: string): Uint8Array {
  const binary = atob(value);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

async function encryptionKey(scope: string): Promise<CryptoKey> {
  const keyName = keyStorageKey(scope);
  let encoded = sessionStorage.getItem(keyName);
  if (!encoded) {
    encoded = encode(crypto.getRandomValues(new Uint8Array(32)));
    sessionStorage.setItem(keyName, encoded);
  }
  return crypto.subtle.importKey(
    "raw",
    decode(encoded).buffer as ArrayBuffer,
    { name: "AES-GCM" },
    false,
    ["encrypt", "decrypt"],
  );
}

export async function saveLocalClinicalDraft(
  scope: string,
  draft: LocalClinicalDraft,
): Promise<boolean> {
  try {
    const key = await encryptionKey(scope);
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const payload = new TextEncoder().encode(
      JSON.stringify({
        version: 1,
        expiresAt: new Date(Date.now() + VAULT_TTL_MS).toISOString(),
        draft,
      }),
    );
    const ciphertext = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, payload);
    const stored: StoredVault = {
      version: 1,
      iv: encode(iv),
      ciphertext: encode(new Uint8Array(ciphertext)),
    };
    localStorage.setItem(storageKey(scope), JSON.stringify(stored));
    return true;
  } catch {
    return false;
  }
}

export async function loadLocalClinicalDraft(scope: string): Promise<LocalClinicalDraft | null> {
  try {
    const raw = localStorage.getItem(storageKey(scope));
    if (!raw) return null;
    const stored = JSON.parse(raw) as StoredVault;
    if (stored.version !== 1) throw new Error("unsupported vault");
    const key = await encryptionKey(scope);
    const plaintext = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: decode(stored.iv).buffer as ArrayBuffer },
      key,
      decode(stored.ciphertext).buffer as ArrayBuffer,
    );
    const value = JSON.parse(new TextDecoder().decode(plaintext)) as {
      version: 1;
      expiresAt: string;
      draft: LocalClinicalDraft;
    };
    if (value.version !== 1 || Date.parse(value.expiresAt) <= Date.now()) {
      clearLocalClinicalDraft(scope);
      return null;
    }
    return value.draft;
  } catch {
    clearLocalClinicalDraft(scope);
    return null;
  }
}

export function clearLocalClinicalDraft(scope: string): void {
  try {
    localStorage.removeItem(storageKey(scope));
  } catch {
    // Storage can be unavailable in private browsing.
  }
}
